/* eslint-disable no-underscore-dangle */
import { useState, ChangeEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import * as R from 'ramda';

import { isFilesSizeValid } from 'CorePackage/MainModule/validators/isFilesSizeValid';
import { isFileExtensionValid } from 'CorePackage/MainModule/validators/isFileExtensionValid';
import { IUploadFile } from 'CorePackage/MainModule/interfaces/IUploadFile';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { KNM_FILE_SIZE_ERROR_BIG, KNM_FILE_SIZE_ERROR_SMALL } from 'CorePackage/MainModule/constants/notices';
import { addNoticeKnmFileSizeErrorBig } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorBig';
import { addNoticeKnmFileSizeErrorSmall } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';

import styles from './UploadInput.module.scss';

const ERROR_SIZE = 'Суммарный объем файлов не должен превышать 40MB';
const ERROR_EXT = 'Недопустимый формат файла';
const validExtensions = [
  'ods',
  'doc',
  'docx',
  'jpeg',
  'jpg',
  'pdf',
  'png',
  'rar',
  'xlsx',
  'xls',
  'zip',
  'txt',
  'rtf',
  'mp4',
];

export interface IUploadInputProps {
  files: IUploadFile[];
  error?: string;
  invalidExtensions?: string[];
  fileToNews?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>, fileName?: string) => void;
  setError?: (error: string) => void;
  addNotice: (notice: INotification) => void;
  label?: string;
  disabled?: boolean;
}

const UploadInputWithoutStore = ({
  files,
  invalidExtensions,
  error,
  fileToNews,
  onChange,
  setError,
  addNotice,
  label,
  disabled,
}: IUploadInputProps) => {
  const [sameNameCount, setSameNameCount] = useState(1);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    const targetFiles = e.target.files;

    if (!targetFiles) {
      return;
    }

    const file = targetFiles[0];
    const fileName = file.name;
    const ext =
      invalidExtensions && invalidExtensions.length > 0
        ? R.reject(R.contains(R.__, invalidExtensions), validExtensions)
        : validExtensions;

    if (!isFileExtensionValid(fileName, ext)) {
      addNotice({
        renderContent: ERROR_EXT,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.FILE_FORMATE,
      });

      return;
    }

    if (!isFilesSizeValid(files, file.size)) {
      addNotice({
        renderContent: ERROR_SIZE,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.FILE_SIZE,
      });
      setError && setError(ERROR_SIZE);

      return;
    }

    if (fileToNews && file.size > 10 * MB) {
      dispatch(addNoticeKnmFileSizeErrorBig());
      setError && setError(KNM_FILE_SIZE_ERROR_BIG);

      return;
    }

    if (!fileToNews && file.size > 5 * MB) {
      dispatch(addNoticeKnmFileSizeErrorSmall());
      setError && setError(KNM_FILE_SIZE_ERROR_SMALL);

      return;
    }
    setError && setError('');

    let nameIfFileExists;

    if (files.find((f: IUploadFile) => f.fileName === fileName)) {
      const index = fileName.lastIndexOf('.');

      nameIfFileExists = `${fileName.slice(0, index)}(${sameNameCount})${fileName.slice(index)}`;
      setSameNameCount(sameNameCount + 1);
    }

    onChange(e, nameIfFileExists);
    e.target.value = '';
  }

  return (
    <div className={styles.Block}>
      <div>
        <span className={styles.Label}>{label || 'Документы'}</span>
        {!disabled ? (
          <label className={styles.LoadButton}>
            Добавить
            <input className={styles.Input} type="file" name="load" onChange={handleFileChange} />
          </label>
        ) : (
          <label className={styles.DisabledLoadButton}>Добавить</label>
        )}
      </div>
      {error && <div className={styles.Error}>{error}</div>}
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<RootActions>) => ({
  addNotice: (notice: INotification) => dispatch(addNoticeAction(notice)),
});

export const UploadInput = connect(null, mapDispatchToProps)(UploadInputWithoutStore);
